import { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [plan, setPlan] = useState('free');
  const [loading, setLoading] = useState(true);

  const loadUserPlan = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('user_plans')
        .select('plan')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        console.error('loadUserPlan error:', error);
        setPlan('free');
        return;
      }

      setPlan(data?.plan || 'free');
    } catch (error) {
      console.error('loadUserPlan catch:', error);
      setPlan('free');
    }
  };

  useEffect(() => {
    let mounted = true;

    const initAuth = async () => {
      try {
        const timeoutPromise = new Promise((resolve) => {
          setTimeout(() => resolve(null), 4000);
        });

        const sessionPromise = supabase.auth.getSession();

        const result = await Promise.race([sessionPromise, timeoutPromise]);

        if (!mounted) return;

        const session = result?.data?.session || null;

        if (session?.user) {
          setUser(session.user);
          await loadUserPlan(session.user.id);
        } else {
          setUser(null);
          setPlan('free');
        }
      } catch (error) {
        console.error('initAuth error:', error);
        if (mounted) {
          setUser(null);
          setPlan('free');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    initAuth();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!mounted) return;

      if (session?.user) {
        setUser(session.user);
      } else {
        setUser(null);
        setPlan('free');
      }

      setLoading(false);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!user?.id) return;

    let cancelled = false;

    const loadPlanSafely = async () => {
      await new Promise((resolve) => setTimeout(resolve, 0));

      if (cancelled) return;

      await loadUserPlan(user.id);
    };

    loadPlanSafely();

    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const signUp = async (email, password, name) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { name } },
    });

    if (error) throw error;

    if (data.user) {
      await supabase.from('user_plans').upsert({
        user_id: data.user.id,
        plan: 'free',
        email,
        name,
      });
    }

    return data;
  };

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) throw error;

    return data;
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setPlan('free');
  };

  const upgradePlan = async (newPlan) => {
    if (!user) return;

    setPlan(newPlan);

    const { error } = await supabase.from('user_plans').upsert({
      user_id: user.id,
      plan: newPlan,
      email: user.email,
    });

    if (error) {
      console.error('upgradePlan error:', error);
    }
  };

  const isPaid = plan === 'starter' || plan === 'pro';
  const isPro = plan === 'pro';
  const userName = user?.user_metadata?.name || user?.email?.split('@')[0] || 'there';

  return (
    <AuthContext.Provider
      value={{
        user,
        plan,
        loading,
        userName,
        signUp,
        signIn,
        logout,
        upgradePlan,
        isPaid,
        isPro,
      }}
    >
      {loading ? (
        <div
          style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#f8faff',
            flexDirection: 'column',
            gap: 16,
          }}
        >
          <img
            src="/pilotleadgen-logo.png"
            alt="PilotLeadGen"
            style={{
              width: 48,
              height: 48,
              objectFit: 'contain',
              animation: 'pulse 1.5s ease-in-out infinite',
            }}
          />
          <div style={{ fontSize: 14, color: '#94a3b8', fontFamily: 'sans-serif' }}>
            Loading PilotLeadGen...
          </div>
          <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }`}</style>
        </div>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}